import { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface PosFullscreenContextValue {
  posAppFullscreen: boolean;
  toggleAppFS: () => void;
  toggleBrowserFS: () => void;
}

export const PosFullscreenContext = createContext<PosFullscreenContextValue>({
  posAppFullscreen: false,
  toggleAppFS: () => {},
  toggleBrowserFS: () => {},
});

export const usePosFullscreen = () => useContext(PosFullscreenContext);

interface Props {
  children: React.ReactNode;
}

export function PosFullscreenProvider({ children }: Props) {
  const [posAppFullscreen, setPosAppFullscreen] = useState(false);

  const toggleAppFS = useCallback(() => {
    setPosAppFullscreen((v) => !v);
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  const toggleBrowserFS = useCallback(async () => {
    if (!document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen();
        setPosAppFullscreen(true);
      } catch {
        // Browser denied fullscreen
      }
    } else {
      await document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      if (!document.fullscreenElement) {
        setPosAppFullscreen(false);
      }
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  return (
    <PosFullscreenContext.Provider value={{ posAppFullscreen, toggleAppFS, toggleBrowserFS }}>
      {children}
    </PosFullscreenContext.Provider>
  );
}
